import { useTranslation } from 'react-i18next';

import { cn, ToggleButtonItem, ToggleButtons } from '@kognitos/lattice';

import type { TAssetDocumentView } from './asset-views';

/**
 * Props for {@link AssetVersionSwitch}.
 */
export interface IAssetVersionSwitchProps {
  value: TAssetDocumentView;
  onValueChange: (value: TAssetDocumentView) => void;
  /** The published version number, without the `v`. */
  liveVersion?: string;
  /** The draft's version, if it has one ahead of the live one. */
  draftVersion?: string;
  /** Dims Live for a turned-off asset. */
  isLiveDeactivated?: boolean;
  'data-testid'?: string;
}

const ITEM_CLASS =
  'relative z-10 h-6 gap-1.5 rounded-full px-3 text-xs text-muted-foreground transition-colors hover:bg-transparent hover:text-foreground data-[state=on]:bg-transparent';

/**
 * The Draft | Live switch in an asset document's title bar while the asset has
 * both a live version and a draft, in place of `badges`. A thumb slides to the
 * version in view, green on Live and blue on Draft. The page decides what
 * showing each one means.
 */
export function AssetVersionSwitch({
  value,
  onValueChange,
  liveVersion,
  draftVersion,
  isLiveDeactivated,
  'data-testid': dataTestId = 'asset-version-switch',
}: IAssetVersionSwitchProps) {
  const { t } = useTranslation('common');
  const isLive = value === 'live';
  const liveLabel = liveVersion
    ? t('common.assetStage.liveVersion', { version: liveVersion })
    : t('common.assetStage.live');
  const draftLabel = draftVersion
    ? t('common.assetStage.draftVersion', { version: draftVersion })
    : t('common.assetStage.draft');

  // TODO: Use `ToggleButtons variant="segmented"` with item `tone`s once
  // kognitos/lattice#189 is released, and drop the thumb and classes below.
  return (
    <ToggleButtons
      value={value}
      onValueChange={next => {
        if ((next === 'live' || next === 'draft') && next !== value)
          onValueChange(next);
      }}
      aria-label={t('common.assetStage.versionSwitch')}
      className="relative ml-1 grid h-7 shrink-0 grid-cols-2 gap-0 rounded-full bg-muted p-0.5"
      data-testid={dataTestId}
    >
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0.5 left-0.5 w-[calc(50%-0.125rem)] overflow-hidden rounded-full border bg-background shadow-xs transition-[translate,border-color] duration-200 ease-out motion-reduce:transition-none',
          isLive
            ? 'translate-x-full border-success/40'
            : 'border-informative/40',
        )}
      >
        <span
          className={cn(
            'absolute inset-0 transition-colors duration-200 motion-reduce:transition-none',
            isLive ? 'bg-success/10' : 'bg-informative/10',
          )}
        />
      </span>
      <ToggleButtonItem
        value="draft"
        aria-label={draftLabel}
        className={cn(ITEM_CLASS, 'data-[state=on]:text-informative')}
        data-testid={`${dataTestId}-draft`}
      >
        {t('common.assetStage.draft')}
      </ToggleButtonItem>
      <ToggleButtonItem
        value="live"
        aria-label={liveLabel}
        className={cn(
          ITEM_CLASS,
          'data-[state=on]:text-success',
          isLiveDeactivated && 'opacity-50',
        )}
        data-testid={`${dataTestId}-live`}
      >
        {t('common.assetStage.live')}
      </ToggleButtonItem>
    </ToggleButtons>
  );
}
